import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

export const Route = createFileRoute('/unsubscribe')({
  head: () => ({
    meta: [
      { title: 'Se désinscrire — Rénergescence' },
      { name: 'robots', content: 'noindex,nofollow' },
    ],
  }),
  component: UnsubscribePage,
})

type State =
  | { status: 'loading' }
  | { status: 'ready' }
  | { status: 'already' }
  | { status: 'invalid' }
  | { status: 'submitting' }
  | { status: 'done' }
  | { status: 'error'; message: string }

function UnsubscribePage() {
  const [state, setState] = useState<State>({ status: 'loading' })
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const t = params.get('token')
    if (!t) {
      setState({ status: 'invalid' })
      return
    }
    setToken(t)
    ;(async () => {
      try {
        const res = await fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`)
        const data = await res.json()
        if (!res.ok) {
          setState({ status: 'invalid' })
          return
        }
        if (data.valid === false && data.reason === 'already_unsubscribed') {
          setState({ status: 'already' })
          return
        }
        setState({ status: 'ready' })
      } catch {
        setState({ status: 'error', message: 'Erreur réseau' })
      }
    })()
  }, [])

  const confirm = async () => {
    if (!token) return
    setState({ status: 'submitting' })
    try {
      const res = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await res.json()
      if (!res.ok) {
        setState({ status: 'error', message: data?.error || 'Erreur' })
        return
      }
      if (data.success === false && data.reason === 'already_unsubscribed') {
        setState({ status: 'already' })
        return
      }
      setState({ status: 'done' })
    } catch {
      setState({ status: 'error', message: 'Erreur réseau' })
    }
  }

  return (
    <section className="py-24">
      <div className="mx-auto max-w-md px-6 text-center">
        <h1 className="font-display text-4xl font-light italic">Désinscription</h1>
        <div className="mt-8 rounded-2xl border p-8" style={{ borderColor: 'var(--color-border)' }}>
          {state.status === 'loading' && <p>Vérification en cours…</p>}
          {state.status === 'invalid' && (
            <p>Ce lien de désinscription est invalide ou a expiré.</p>
          )}
          {state.status === 'already' && (
            <p>Cette adresse est déjà désinscrite. Vous ne recevrez plus d'e-mails.</p>
          )}
          {state.status === 'ready' && (
            <>
              <p className="mb-6">Confirmez-vous votre désinscription de nos e-mails ?</p>
              <button
                onClick={confirm}
                className="rounded-full px-6 py-3 text-sm font-medium"
                style={{ backgroundColor: 'var(--color-sage)', color: 'var(--color-background)' }}
              >
                Confirmer la désinscription
              </button>
            </>
          )}
          {state.status === 'submitting' && <p>Traitement…</p>}
          {state.status === 'done' && (
            <p>Vous avez été désinscrit. Vous ne recevrez plus d'e-mails.</p>
          )}
          {state.status === 'error' && <p>Une erreur est survenue : {state.message}</p>}
        </div>
      </div>
    </section>
  )
}